import { apiClient, API_ENDPOINTS } from '@dukene/shared';
export { apiClient, API_ENDPOINTS };

export interface CustomerResponse {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  credit_balance?: number;
}

export interface ProductSearchResult {
  id: string;
  name: string;
  sku?: string | null;
  barcode?: string | null;
  base_price: string;
  tax_rate?: string | null;
  is_active?: boolean;
  category?: string | null;
  image_url?: string | null;
  track_stock?: boolean;
  qty_on_hand?: number | null;
  images?: any[];
  image?: any;
}

export interface VATSnapshot {
  pin?: string;
  rate?: number;
  mode?: 'included' | 'added' | 'disabled';
}

export interface DiscountRule {
  id: string;
  name: string;
  type: 'percentage_off' | 'fixed_amount_off';
  value: string;
  applies_to: 'order' | 'product' | 'category';
  product_ids?: string[];
  category_ids?: string[];
  requires_coupon_code: boolean;
  coupon_code?: string | null;
  is_active: boolean;
  starts_at: string;
  expires_at: string;
  maximum_discount_amount?: string | null;
}

export interface OrderLineCreateRequest {
  product_id?: string | null;
  product_name: string;
  qty: number;
  unit_price: number;
  discount_total: number;
  service_charge: number;
}

function getBackendOrigin(): string {
  try {
    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
    if (apiBase && (apiBase.startsWith('http://') || apiBase.startsWith('https://'))) {
      return new URL(apiBase).origin;
    }
  } catch {}
  return 'http://localhost:3000';
}

function extractImageUrl(p: any): string | null {
  let url: string | null = null;
  if (typeof p.image_url === 'string' && p.image_url) url = p.image_url;
  else if (typeof p.imageUrl === 'string' && p.imageUrl) url = p.imageUrl;
  else if (typeof p.image === 'string' && p.image) url = p.image;
  else if (p.image?.url && typeof p.image.url === 'string') url = p.image.url;
  else if (Array.isArray(p.images) && p.images.length > 0) {
    const first = p.images[0];
    if (typeof first === 'string') url = first;
    else if (first && typeof first.url === 'string') url = first.url;
    else if (first && typeof first.src === 'string') url = first.src;
  }
  else if (Array.isArray(p.variants) && p.variants.length > 0) {
    const firstVarWithImg = p.variants.find((v: any) => v.image || v.image_url);
    if (firstVarWithImg) {
      url = typeof firstVarWithImg.image === 'string' ? firstVarWithImg.image : (firstVarWithImg.image?.url || firstVarWithImg.image_url || null);
    }
  }

  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  if (url.startsWith('/')) {
    const origin = getBackendOrigin();
    return `${origin}${url}`;
  }
  return url;
}

export const posApi = {
  async listProducts(params: { limit?: number; storeId?: string; store_id?: string; location_id?: string } = {}): Promise<{ items: ProductSearchResult[] }> {
    try {
      const storeId = params.storeId || params.store_id || params.location_id;
      const res = await apiClient.get<any>(API_ENDPOINTS.PRODUCTS.LIST, {
        params: { storeId, limit: params.limit || 200 },
      });
      const rawList = Array.isArray(res) ? res : res?.products || res?.data?.products || res?.items || (Array.isArray(res?.data) ? res.data : []);
      const items: ProductSearchResult[] = rawList.map((p: any) => ({
        id: p.id,
        name: p.name || p.title || 'Untitled Product',
        sku: p.sku || null,
        barcode: p.barcode || null,
        base_price: String(p.base_price || p.price || 0),
        tax_rate: String(p.tax_rate || 0.16),
        is_active: p.isActive !== false && p.status !== 'ARCHIVED',
        category: p.category?.name || p.category || 'General',
        image_url: extractImageUrl(p),
        track_stock: p.trackInventory !== false,
        qty_on_hand: p.inventory?.available ?? p.inventory?.quantity ?? p.stockQuantity ?? 100,
        images: p.images || (p.image ? [p.image] : []),
      } as any));
      return { items };
    } catch {
      return { items: [] };
    }
  },

  async searchProducts(query: string, limit: number = 10): Promise<ProductSearchResult[]> {
    try {
      const res = await apiClient.get<any>(API_ENDPOINTS.PRODUCTS.LIST, {
        params: { search: query, limit },
      });
      const rawList = Array.isArray(res) ? res : res?.products || res?.data?.products || res?.items || (Array.isArray(res?.data) ? res.data : []);
      return rawList.map((p: any) => ({
        id: p.id,
        name: p.name || p.title || 'Untitled Product',
        sku: p.sku || null,
        barcode: p.barcode || null,
        base_price: String(p.base_price || p.price || 0),
        tax_rate: String(p.tax_rate || 0.16),
        is_active: p.isActive !== false && p.status !== 'ARCHIVED',
        category: p.category?.name || p.category || 'General',
        image_url: extractImageUrl(p),
        track_stock: p.trackInventory !== false,
        qty_on_hand: p.inventory?.available ?? p.inventory?.quantity ?? p.stockQuantity ?? 100,
        images: p.images || (p.image ? [p.image] : []),
        image: p.image,
      }));
    } catch {
      return [];
    }
  },

  async createOrder(data: { storeId?: string; store_id?: string; location_id?: string; session_id?: string; status?: string; lines?: any[] }): Promise<{ id: string }> {
    try {
      if (!data.lines || data.lines.length === 0) {
        return { id: `pos-draft-${Date.now()}` };
      }
      const storeId = data.storeId || data.store_id || data.location_id;
      const res = await apiClient.post<any>('/pos/orders', {
        storeId,
        source: 'pos',
        status: data.status || 'draft',
        items: data.lines.map((l: any) => ({
          productId: l.product_id !== undefined ? l.product_id : (l.product?.id?.startsWith('service-') ? null : l.product?.id),
          title: l.product_name || l.product?.name || 'Custom Item',
          quantity: l.qty || 1,
          price: l.unit_price || 0,
        })),
      }, {
        headers: {
          'X-POS-Terminal-ID': data.session_id || 'unknown'
        }
      });
      const returnedId = res?.id || res?.data?.id || res?.order?.id;
      return { id: returnedId || `pos-draft-${Date.now()}` };
    } catch {
      return { id: `pos-draft-${Date.now()}` };
    }
  },

  async persistDraftForPayment(orderId: string | null, data: any): Promise<string> {
    try {
      const storeId = data.storeId || data.store_id || data.location_id;
      const isLocalDraft = !orderId || orderId.startsWith('pos-');

      if (isLocalDraft) {
        const res = await apiClient.post<any>('/pos/orders', {
          storeId,
          customerId: data.customer_id,
          source: 'pos',
          status: 'pending',
          email: data.customer?.email || 'pos-walkin@dukene.local',
          totalAmount: data.totalAmount || 0,
          paymentMethod: 'mobile_money',
          items: (data.lines || []).map((l: any) => ({
            productId: l.product_id || (l.product?.id?.startsWith('service-') ? null : l.product?.id),
            title: l.product_name || l.product?.name || 'Custom Item',
            quantity: l.qty || 1,
            price: l.unit_price || 0,
          })),
        }, {
          headers: {
            'X-POS-Terminal-ID': data.session_id || 'unknown'
          }
        });
        const returnedId = res?.id || res?.data?.id || res?.order?.id;
        if (returnedId) return returnedId;
      }
      return orderId || `pos-draft-${Date.now()}`;
    } catch (e: any) {
      throw new Error(e.message || 'Could not save order before initiating STK Push');
    }
  },

  async updateOrder(orderId: string, data: any): Promise<void> {
    try {
      const storeId = data.storeId || data.store_id || data.location_id;
      const isLocalDraft = !orderId || orderId.startsWith('pos-');

      if (data.status === 'completed' && isLocalDraft) {
        await apiClient.post<any>('/pos/orders', {
          storeId,
          customerId: data.customer_id,
          source: 'pos',
          status: 'completed',
          email: data.customer?.email || 'pos-walkin@dukene.local',
          totalAmount: data.payments?.[0]?.amount || 0,
          paymentMethod: data.payments?.[0]?.method || 'cash',
          items: (data.lines || []).map((l: any) => ({
            productId: l.product_id !== undefined ? l.product_id : (l.product?.id?.startsWith('service-') ? null : l.product?.id),
            title: l.product_name || l.product?.name || 'Custom Item',
            quantity: l.qty || 1,
            price: l.unit_price || 0,
          })),
        }, {
          headers: {
            'X-POS-Terminal-ID': data.session_id || 'unknown'
          }
        });
        return;
      }

      await apiClient.patch(API_ENDPOINTS.ORDERS.UPDATE(orderId), {
        storeId,
        status: data.status,
        customerId: data.customer_id,
        items: data.lines?.map((l: any) => ({
          productId: l.product_id || (l.product?.id?.startsWith('service-') ? null : l.product?.id),
          title: l.product_name || l.product?.name || 'Custom Item',
          quantity: l.qty || 1,
          price: l.unit_price || 0,
        })),
        paymentMethod: data.payments?.[0]?.method || undefined,
        totalAmount: data.payments?.[0]?.amount || undefined,
      }, {
        headers: {
          'X-POS-Terminal-ID': data.session_id || 'unknown'
        }
      });
    } catch {}
  },

  async voidOrder(orderId: string): Promise<void> {
    try {
      if (orderId && !orderId.startsWith('pos-draft-')) {
        await apiClient.post(API_ENDPOINTS.ORDERS.CANCEL(orderId));
      }
    } catch {}
  },
};

export const offersApi = {
  async listDiscountRules(_storeId?: string): Promise<DiscountRule[]> {
    return [];
  },

  async validateCoupon(_storeId: string, payload: { coupon_code: string; cart_total: number; items?: any[] }): Promise<{ valid: boolean; discount_amount: number; message: string }> {
    if (payload.coupon_code.toUpperCase() === 'WELCOME10') {
      const discount = payload.cart_total * 0.1;
      return { valid: true, discount_amount: discount, message: '10% Welcome Discount applied!' };
    }
    return { valid: false, discount_amount: 0, message: 'Invalid or expired coupon code.' };
  },

  async getLoyaltyProgram(_storeId?: string): Promise<any> {
    return null;
  },
};

export const accountingApi = {
  async listAccountsForCheckout(_storeId?: string): Promise<Array<{
    id: string;
    name: string;
    code: string;
    account_type: string;
    opening_balance: string;
    balance: string;
    is_system: boolean;
    is_active: boolean;
    created_at: string;
  }>> {
    return [
      { id: 'acc-cash', name: 'Cash Till Account', code: 'cash', account_type: 'asset', opening_balance: '0', balance: '50000', is_system: true, is_active: true, created_at: new Date().toISOString() },
      { id: 'acc-mpesa', name: 'M-Pesa Paybill / Till', code: 'mobile_money', account_type: 'revenue', opening_balance: '0', balance: '120000', is_system: true, is_active: true, created_at: new Date().toISOString() },
      { id: 'acc-credit', name: 'Customer Credit Ledger', code: 'credit', account_type: 'liability', opening_balance: '0', balance: '0', is_system: true, is_active: true, created_at: new Date().toISOString() },
      { id: 'acc-bank', name: 'Bank Card Transfer', code: 'bank', account_type: 'equity', opening_balance: '0', balance: '300000', is_system: true, is_active: true, created_at: new Date().toISOString() },
    ];
  },
};
